import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  Code as CodeIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  Help as UnknownIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const StatCard = ({ title, value, icon, loading }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ padding: { xs: 1.5, sm: 2 } }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item>{icon}</Grid>
        <Grid item>
          <Typography color="textSecondary" gutterBottom variant="body2" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>{value}</Typography>
          )}
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user: currentUser } = useAuth();
  const [stats, setStats] = useState({
    users: 0,
    clients: 0,
    products: 0,
    implementations: 0,
  });
  const [statusList, setStatusList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(true);
    
  const isAdmin = () => {
    console.log('Current user role:', currentUser.role); // Agrega esta línea para depuració
    return ['Administrador', 'Sistemas'].includes(currentUser.role);
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [users, clients, products, implementations] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/users`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/clients`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/products`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/implementations`),
        ]);

        setStats({
          users: users.data.length,
          clients: clients.data.length,
          products: products.data.length,
          implementations: implementations.data.length,
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchStatusList = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/status`);
        setStatusList(response.data);
      } catch (error) {
        console.error('Error fetching implementation status:', error);
      } finally {
        setLoadingStatus(false);
      }
    };

    fetchStats();
    fetchStatusList();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <OnlineIcon style={{ color: 'green' }} />;
      case 'offline':
        return <OfflineIcon style={{ color: 'red' }} />;
      default:
        return <UnknownIcon style={{ color: 'gray' }} />;
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      '1': 'Implementado',
      '2': 'En proceso de actualización',
      '3': 'En proceso de implementación',
      '4': 'Dado de baja',
      '5': 'Baja temporal',
    };
    return statusMap[status] || status;
  };

  return (
    <div>
      <Typography variant="h5" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }} gutterBottom>
        Panel de control
      </Typography>
      <Grid container spacing={2}>
        {(isAdmin()) && (
          <>
            <Grid item xs={6} sm={6} md={3}>
              <StatCard
                title="Usuarios"
                value={stats.users}
                icon={<PeopleIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />}
                loading={loading}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <StatCard
                title="Clientes"
                value={stats.clients}
                icon={<BusinessIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />}
                loading={loading}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <StatCard
                title="Productos"
                value={stats.products}
                icon={<InventoryIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />}
                loading={loading}
              />
            </Grid>
          </>
        )}
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Implementaciones"
            value={stats.implementations}
            icon={<CodeIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />}
            loading={loading}
          />
        </Grid>

        {/* Status de implementaciones */}
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 1, sm: 2 }, mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Estado de implementaciones
            </Typography>
            {loadingStatus ? (
              <CircularProgress />
            ) : (
              <Box>
                {/* Vista para dispositivos móviles (cards) */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1 }}>
                  {statusList.map((impl) => (
                    <Paper key={impl.id} elevation={2} sx={{ p: 1.5, borderRadius: 1 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            {impl.client_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Producto:
                          </Typography>
                          <Typography variant="body2">
                            {impl.product_name}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Estado:
                          </Typography>
                          <Typography variant="body2">
                            {getStatusLabel(impl.status)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Versión:
                          </Typography>
                          <Typography variant="body2">
                            {impl.version}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Conexión:
                          </Typography>
                          <Chip
                            icon={getStatusIcon(impl.control_status)}
                            label={impl.control_status}
                            color={impl.control_status === 'online' ? 'success' : impl.control_status === 'offline' ? 'error' : 'default'}
                            size="small"
                            sx={{ height: 24, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Box>
                
                {/* Vista para tablets y desktop (tabla) */}
                <TableContainer sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Cliente</TableCell>
                        <TableCell>Producto</TableCell>
                        <TableCell>Entorno</TableCell>
                        <TableCell>Versión</TableCell>
                        <TableCell>Venc. licencia</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Fecha de control</TableCell>
                        <TableCell>Conexión</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {statusList.map((impl) => (
                        <TableRow key={impl.id}>
                          <TableCell>{impl.client_name}</TableCell>
                          <TableCell>{impl.product_name}</TableCell>
                          <TableCell>{impl.environment}</TableCell>
                          <TableCell>{impl.version}</TableCell>
                          <TableCell>{impl.license_expiry_date.split('T')[0]}</TableCell>
                          <TableCell>{getStatusLabel(impl.status)}</TableCell>
                          <TableCell>{impl.last_check}</TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(impl.control_status)}
                              label={impl.control_status}
                              color={impl.control_status === 'online' ? 'success' : impl.control_status === 'offline' ? 'error' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default Dashboard;